import API_PATHS from '@constants/apiPaths';
import ERRORS from '@constants/error';
import { ERROR_CODES } from '@constants/errors';
import NEXT_API_PATHS from '@constants/nextApiPaths';
import { authHttp, centralHttp } from '@http/index';
import { getReCaptchaToken } from '@utils/recaptcha';
import axios from 'axios';
import { FormikHelpers } from 'formik';
import { find } from 'lodash';
import router from 'next/router';
import { useState } from 'react';
import { PdpaValue } from 'src/account/pdpaForm.schema';
import useTranslation from 'src/i18n/useTranslation';
import { login as authLogin } from '../app-state/auth';
import { ISignup } from './SignupV2';

interface IValidateSignup {
  email: string;
  phoneNumber: string;
}

const SIGNUP_STEP = 0;
const TERM_AND_CON_STEP = 1;
const PDPA_STEP = 2;
const SIGNUP_SUCCESS_STEP = 3;

export function useSignup() {
  const [step, setStep] = useState<number>(SIGNUP_STEP);
  const [error, setError] = useState(null);
  const [signupPayload, setSignupPayload] = useState<ISignup>(null);
  const { t } = useTranslation();

  const validateSignupForm = async (
    values: ISignup,
    helpers: FormikHelpers<IValidateSignup>,
  ) => {
    setError('');
    try {
      const { email, phoneNumber } = values;
      await authHttp.post(API_PATHS.VALIDATE_SIGNUP, {
        email,
        phoneNumber,
      });
      setStep(TERM_AND_CON_STEP);
      setSignupPayload(values);
    } catch (error) {
      if (error && error.response && error.response.status === 400) {
        if (error.response.data.errors) {
          if (
            find(error.response.data.errors, ERROR_CODES.EMAIL_ALREADY_EXIST)
          ) {
            helpers.setFieldError('email', t('signupPage.emailExists'));
          }
          if (
            find(error.response.data.errors, ERROR_CODES.PHONE_NUM_ALREADY_USED)
          ) {
            helpers.setFieldError(
              'phoneNumber',
              t('signupPage.phoneNumExists'),
            );
          }
        } else {
          setError(error.response.data.error);
        }
      } else {
        setError(ERRORS.GENERIC_NETWORK_ERROR);
      }
    }
  };

  const submitSignupForm = async (
    values: PdpaValue,
    helpers: FormikHelpers<IValidateSignup>,
  ) => {
    try {
      setError('');
      const recaptcha = await getReCaptchaToken('signup_local');
      const response = await axios.post(NEXT_API_PATHS.SIGN_UP_LOCAL, {
        ...signupPayload,
        phoneNumber: signupPayload.phoneNumber || null,
        recaptcha,
      });
      const { accessToken, accessTokenExpiry, user } = response.data;

      await authLogin(
        {
          jwtToken: accessToken,
          jwtTokenExpiry: accessTokenExpiry,
          user,
        },
        true,
        (router.query.referer as string) ?? undefined,
      );

      await centralHttp.post(API_PATHS.USER_PDPA_AGREEMENT, {
        ...values,
      });

      setStep(SIGNUP_SUCCESS_STEP);
    } catch (error) {
      if (error && error.response && error.response.status === 400) {
        if (error.response.data.data) {
          setError(error.response.data.data?.error);
        } else {
          setError(error.response.data.error);
        }
      } else {
        setError(ERRORS.GENERIC_NETWORK_ERROR);
      }
      console.log(error);
    }
  };

  const handleSubmitTermandCon = () => {
    setStep(PDPA_STEP);
  };
  return {
    validateSignupForm,
    submitSignupForm,
    error,
    step,
    handleSubmitTermandCon,
  };
}
